import cuid from 'cuid'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { bindActionCreators } from 'redux'
import type { Artist, ArtistId } from 'types/artist'
import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import type { Track } from 'types/track'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  loadArtistShowcases,
  loadArtistTracks,
  loadRelatedArtists,
} from '@disco/state-management/actions/artist-actions'
import {
  appendTracksToPlaylist,
  loadPane,
  togglePlaylistInLibrary,
  updatePane,
} from '@disco/state-management/actions/ExplorerActions'
import { addNestedTracksToMain } from '@disco/state-management/actions/NestingActions'
import {
  resetSelection,
  selectExplorerItems,
} from '@disco/state-management/actions/SelectionActions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import { SelectionProvider } from 'contexts/selection'
import { getSelectedLibrary } from '@disco/state-management/selectors/app'
import {
  getArtist,
  getArtistShowcases,
  getArtistTracks,
  getRelatedArtists,
} from '@disco/state-management/selectors/artists'
import { getGroups } from '@disco/state-management/selectors/groups'
import {
  getAllSelectedItems,
  getExplorerSelection,
  getSelectionIndexes,
} from '@disco/state-management/selectors/selection'
import ExplorerTrackItem from 'components/explorer/track-item'
import InfiniteScroll from 'components/ui/infinite-scroll'
import type { PaneItemAppearance } from 'components/ui/pane-item'
import PaneItems from '../pane-items'
import styles from './index.module.css'

const getPaneItemAppearance = (
  pane: ExplorerPane,
  paneSelection: number[],
  index: number,
  itemPrefix: string,
): PaneItemAppearance => {
  if (paneSelection.includes(index)) {
    return 'selected'
  } else if (pane.activeItem == `${itemPrefix}/${index}`) {
    return 'active'
  } else {
    return 'default'
  }
}

type Props = {
  artistId: ArtistId
  pane: ExplorerPane
  children?: React.ReactNode
}

const ArtistPane = ({ artistId, pane, children }: Props) => {
  const dispatch = useDispatch()
  const artist: Artist = useSelector((state: State) =>
    getArtist(state, artistId),
  )
  const user = useSelector(state => state.user)
  const componentHeight = useSelector(
    state => state.user.settings.explorer_component_height,
  )
  const groups: Group[] = useSelector(getGroups)
  const explorerSelection: Record<string, any> =
    useSelector(getExplorerSelection)
  const [selectionId] = useState(cuid())
  const paneSelection = useSelector(state =>
    getSelectionIndexes(state, selectionId),
  )
  const selectedItems = useSelector(getAllSelectedItems)
  const relatedArtists: Artist[] = useSelector((state: State) =>
    getRelatedArtists(state, artistId),
  )
  const selectedLibrary: Group | null | undefined =
    useSelector(getSelectedLibrary)
  const showcases: Playlist[] = useSelector((state: State) =>
    getArtistShowcases(state, artistId),
  )
  const tracks = useSelector((state: State) => getArtistTracks(state, artistId))
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const loadData = useCallback(
    async (artistId: ArtistId) => {
      setIsLoading(true)

      try {
        await Promise.all([
          dispatch(loadArtistShowcases(artistId)),
          dispatch(loadRelatedArtists(artistId)),
          dispatch(loadArtistTracks(artistId)),
        ])
      } catch (error) {}

      setIsLoading(false)
    },
    [dispatch],
  )
  useEffect(() => {
    loadData(artistId)
  }, [artistId])
  const loadNextTracks = useCallback(
    (next: string) => {
      dispatch(loadArtistTracks(artistId, next))
    },
    [artistId, dispatch],
  )
  return isLoading ? (
    <div className={styles.artistPaneSection}>Loading...</div>
  ) : (
    <>
      {artist.showcase_playlist_exists && (
        <>
          <div className={styles.artistPaneSection}>Pages</div>
          <PaneItems // TODO: loadNext - implement paging for ArtistPane
            loadNext={() => {}}
            isLoading={false}
            componentHeight={componentHeight}
            items={showcases}
            selectedItems={selectedItems}
            library={selectedLibrary}
            groups={groups}
            // @ts-expect-error check if needed
            explorerSelection={explorerSelection}
            pane={pane}
            user={user}
            itemPrefix="artist-pages"
            {...bindActionCreators(
              {
                loadPane,
                updatePane,
                resetSelection,
                selectExplorerItems,
                createNewShortlist,
                sendMixpanelEvent,
                appendTracksToPlaylist,
                togglePlaylistInLibrary,
                addNestedTracksToMain,
              },
              dispatch,
            )} // onDrop={onDrop}
            // shareableChannelsEnabled={props.shareableChannelsEnabled}
            // {...props}
          />
          {/* {showcases.map((playlist: Playlist, index: number) => ( */}
          {/*   <ExplorerPlaylist */}
          {/*     appearance={getPaneItemAppearance(pane, paneSelection, index, playlist.playlist_id)} */}
          {/*     groups={groups} */}
          {/*     index={index} */}
          {/*     key={playlist.playlist_id} */}
          {/*     library={selectedLibrary ?? undefined} */}
          {/*     pane={pane} */}
          {/*     playlist={playlist} */}
          {/*     showcase */}
          {/*     user={user} */}
          {/*   /> */}
          {/* ))} */}
        </>
      )}
      {relatedArtists.length > 0 && (
        <>
          <div className={styles.artistPaneSection}>Linked Artists</div>
          <div className={styles.artistPaneSection}>
            {relatedArtists.map((artist: Artist) => (
              <div
                className={styles.artistPaneLink}
                key={artist.id}
                translate="no"
                onClick={() => {
                  dispatch(
                    loadPane({
                      artistId: artist.id,
                      index: pane.index + 1,
                      title: artist.name,
                      type: 'artist',
                      url: `/api/artist/${artist.id}/`,
                    }),
                  )
                }}
              >
                {artist.name}
              </div>
            ))}
          </div>
        </>
      )}
      {tracks && tracks.list.length > 0 && (
        <>
          <div className={styles.artistPaneSection}>Tracks</div>
          <InfiniteScroll next={tracks.next} loadNext={loadNextTracks}>
            <SelectionProvider
              value={{
                id: selectionId,
                data: tracks.list,
                paneIndex: pane.index,
              }}
            >
              {tracks.list.map((track: Track, index: number) => (
                <ExplorerTrackItem
                  key={track.id}
                  appearance={getPaneItemAppearance(
                    pane,
                    paneSelection,
                    index,
                    'artist-tracks',
                  )}
                  index={index}
                  itemPrefix="artist-tracks"
                  pane={pane}
                  sourcePlaylistId={null}
                  track={track}
                />
              ))}
            </SelectionProvider>
          </InfiniteScroll>
        </>
      )}
    </>
  )
}

export default React.memo<Props>(ArtistPane)
